import { useEffect, useState } from "react";
import { Divider } from "antd";
import AvailabilityForm from "./components/AvailabilityForm";
import ConductInterviewToggle from "./components/ConductInterviewToggle";
import UnavailabilityForm from "./components/UnavailabilityForm";
import AvailabilityOverflow from "./components/AvailabilityOverflow";
import { InterviewSettingsEntity } from "../../Entities/InterviewSettings";
import { InterviewSettingsDTO } from "../../Dto/InterviewSettings.dto";
import interviewSettingsService from "../../Service/interview-settings.service";
import { openNotification } from "../../Components/Notification";
import userProvidersService from "../../Service/user-providers.service";
import { UserProvider } from "../../Entities/UserProvider";
import { useNotification } from "../../Context/NotificationContext";
import TourOverlay from "../../Components/Tour/TourOverlay";
import InterviewSettingsPageHeader from "./components/InterviewSettingsPageHeader";
import useOrganization from "../../Hooks/useOrganization";
import { useLoader } from "../../Context/LoaderContext";
import "./index.scss";

const InterviewSettings = () => {
  const [settings, setSettings] = useState<InterviewSettingsEntity>();
  const [userProviders, setUserProviders] = useState<UserProvider | null>(null);
  const { org } = useOrganization();
  const { setShow } = useNotification();
  const { setLoading, setLoadingMessage } = useLoader();

  const fetchInterviewSettings = async () => {
    try {
      const response = await interviewSettingsService.getInterviewSettings();
      return response;
    } catch (error) {
      console.log("🚀 ~ fetchInterviewSettings ~ error:", error);
    }
  };

  const fetchUserProviders = async () => {
    try {
      const response = await userProvidersService.getProviders();
      return response;
    } catch (error) {
      console.error("🚀 ~ fetchUserProviders ~ error:", error);
    } finally {
    }
  };

  const updateSettings = async (
    data: InterviewSettingsEntity,
    availableSlotsChanged: boolean,
    unavailableSlotsChanged: boolean
  ) => {
    try {
      const payLoad: InterviewSettingsDTO = {
        availableForInterviews: data?.availableForInterviews,
        availableSlots: data?.availableSlots,
        unavailableSlots: data?.unavailableSlots,
        timeZone: data?.timeZone,
        interviewGuests: data?.interviewGuests?.map((guest) => guest?.email),
        addEventsToCalendar: data?.addEventsToCalendar,
      };
      const response = await interviewSettingsService.updateInterviewSettings(
        payLoad,
        availableSlotsChanged,
        unavailableSlotsChanged
      );
      console.debug("🚀 ~ InterviewSettings ~ payLoad:", payLoad);
      setSettings(response);
      openNotification(
        "success",
        "Interview settings updated successfully",
        ""
      );
    } catch (error) {
      console.error("🚀 ~ updateSettings ~ error:", error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      setLoadingMessage("Loading interview settings...");
      const [interviewSettingsResponse, userProvidersResponse] =
        await Promise.all([fetchInterviewSettings(), fetchUserProviders()]);
      if (interviewSettingsResponse) {
        setSettings(interviewSettingsResponse);
      }
      setUserProviders(userProvidersResponse);
      setLoading(false);
      setLoadingMessage(null);
      setShow(false);
    };
    initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#conduct-interview-toggle") {
      const checkForElement = setInterval(() => {
        const element = document.getElementById("conduct-interview-toggle");
        if (element) {
          clearInterval(checkForElement);

          const rect = element.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetPosition = rect.top + scrollTop - window.innerHeight / 2;

          // Scroll to the element
          window.scrollTo({
            top: targetPosition,
            behavior: "smooth",
          });

          // Then highlight
          requestAnimationFrame(() => {
            element.classList.add("highlight-field");

            // Remove highlight after animation
            setTimeout(() => {
              element.classList.remove("highlight-field");
            }, 2000);
          });
        }
      }, 100);

      // Cleanup interval after 5 seconds if element is not found
      setTimeout(() => {
        clearInterval(checkForElement);
      }, 5000);
    }
  }, [settings]);

  const handleGoogleSignIn = (success: boolean) => {
    // Handle Google sign in success if needed
    console.log("Google sign in success:", success);
  };

  return (
    <section className="dashboard-page interview-settings-page hide-scroll-bar">
      <InterviewSettingsPageHeader
        handleGoogleSignIn={handleGoogleSignIn}
        userProviders={userProviders}
      />
      <Divider orientation="center" style={{ margin: 0 }} />

      <TourOverlay
        targetId="interview-settings-section"
        className="interview-settings__section"
      >
        <ConductInterviewToggle
          settings={
            settings || {
              availableForInterviews: true,
              timeZone: org?.timeZone || "",
              availableSlots: [],
              unavailableSlots: [],
              interviewGuests: [],
              addEventsToCalendar: false,
            }
          }
          updateSettings={updateSettings}
          timeZone={org?.timeZone!}
        />
      </TourOverlay>

      <div className="interview-settings-page-grid">
        <div className="interview-settings-availability-section">
          {settings && settings.availableForInterviews ? (
            <AvailabilityForm
              settings={settings}
              updateSettings={updateSettings}
              timeZone={org?.timeZone!}
            />
          ) : (
            <AvailabilityOverflow message="Availability settings are locked. Enable interview scheduling to configure your availability." />
          )}
        </div>

        <div className="interview-settings-unavailability-section">
          {settings && settings.availableForInterviews ? (
            <UnavailabilityForm
              settings={settings}
              updateSettings={updateSettings}
              timeZone={org?.timeZone!}
            />
          ) : (
            <AvailabilityOverflow message="Unavailability settings are locked. Enable interview scheduling to configure your unavailability." />
          )}
        </div>
      </div>
    </section>
  );
};

export default InterviewSettings;
